import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { getApiErrorMessage, getApiErrorStatus } from '@/services/api/client';
import {
  tracesApi,
  ThreadSummary,
  SpanDetail,
  TraceStats,
  TraceLevelConfig,
  ThreadFilters,
  ThreadInitiator,
  ThreadStatus,
} from '@/services/api/agentApi';

// Build hierarchical span tree from flat array using parent_span_id
function buildSpanTree(spans: SpanDetail[]): SpanDetail[] {
  const map = new Map<string, SpanDetail>();
  const roots: SpanDetail[] = [];

  // Index all spans and reset children
  for (const span of spans) {
    map.set(span.span_id, { ...span, children: [] });
  }

  // Link children to parents
  for (const span of map.values()) {
    if (span.parent_span_id && map.has(span.parent_span_id)) {
      const parentSpan = map.get(span.parent_span_id);
      if (parentSpan) {
        parentSpan.children = [...(parentSpan.children ?? []), span];
      }
    } else {
      roots.push(span);
    }
  }

  return roots;
}

const VALID_THREAD_INITIATORS: ThreadInitiator[] = [
  'user',
  'schedule',
  'trigger',
  'agent',
  'data_source',
  'device',
  'ui_action',
];

const VALID_THREAD_STATUSES: ThreadStatus[] = [
  'active',
  'completed',
  'error',
];

function isThreadInitiator(value: string): value is ThreadInitiator {
  return VALID_THREAD_INITIATORS.includes(value as ThreadInitiator);
}

function isThreadStatus(value: string): value is ThreadStatus {
  return VALID_THREAD_STATUSES.includes(value as ThreadStatus);
}

interface TracesFilters {
  initiator?: string;
  status?: string;
  agentRoute?: string;
  fromDate?: string;
  toDate?: string;
}

interface TracesStoreState {
  threads: ThreadSummary[];
  total: number;
  selectedThread: ThreadSummary | null;
  selectedSpans: SpanDetail[];   // flat list from API
  spanTree: SpanDetail[];         // hierarchical tree
  selectedSpan: SpanDetail | null;
  stats: TraceStats | null;
  traceLevelConfigs: TraceLevelConfig[];
  filters: TracesFilters;
  page: number;
  pageSize: number;
  isLoading: boolean;
  isLoadingSpans: boolean;
  error: string | null;
}

interface TracesStoreActions {
  fetchThreads: () => Promise<void>;
  selectThread: (thread: ThreadSummary) => Promise<void>;
  selectSpan: (span: SpanDetail | null) => void;
  clearSelection: () => void;
  setFilter: (key: keyof TracesFilters, value: string) => void;
  setPage: (page: number) => void;
  fetchStats: (days?: number) => Promise<void>;
  fetchConfig: () => Promise<void>;
  setTraceLevel: (scope: string, scopeId: string, traceLevel: number) => Promise<void>;
  deleteTraceLevel: (scope: string, scopeId: string) => Promise<void>;
}

export const useTracesStore = create<TracesStoreState & TracesStoreActions>()(
  immer((set, get) => ({
    threads: [],
    total: 0,
    selectedThread: null,
    selectedSpans: [],
    spanTree: [],
    selectedSpan: null,
    stats: null,
    traceLevelConfigs: [],
    filters: {},
    page: 0,
    pageSize: 25,
    isLoading: false,
    isLoadingSpans: false,
    error: null,

    fetchThreads: async () => {
      const { filters, page, pageSize } = get();
      set((s) => { s.isLoading = true; s.error = null; });
      try {
        const params: ThreadFilters = {
          limit: pageSize,
          offset: page * pageSize,
        };
        if (filters.initiator && isThreadInitiator(filters.initiator)) {
          params.initiator = filters.initiator;
        }
        if (filters.status && isThreadStatus(filters.status)) {
          params.status = filters.status;
        }
        if (filters.agentRoute) params.agentRoute = filters.agentRoute;
        if (filters.fromDate) params.fromDate = filters.fromDate;
        if (filters.toDate) params.toDate = filters.toDate;

        const result = await tracesApi.listThreads(params);
        console.log('[TracesStore] fetchThreads success:', { total: result.data.total, count: result.data.threads.length });
        set((s) => {
          s.threads = result.data.threads;
          s.total = result.data.total;
          s.isLoading = false;
        });
      } catch (error: unknown) {
        console.error(
          '[TracesStore] fetchThreads failed:',
          getApiErrorMessage(error, 'Failed to fetch threads'),
          getApiErrorStatus(error),
        );
        set((s) => {
          s.error = getApiErrorMessage(error, 'Failed to fetch threads');
          s.isLoading = false;
        });
      }
    },

    selectThread: async (thread: ThreadSummary) => {
      set((s) => {
        s.isLoadingSpans = true;
        s.selectedThread = thread;
        s.selectedSpan = null;
        s.error = null;
      });
      try {
        const result = await tracesApi.getSpans(thread.thread_id);
        const flat = result.data;
        const tree = buildSpanTree(flat);
        set((s) => {
          s.selectedSpans = flat;
          s.spanTree = tree;
          s.isLoadingSpans = false;
        });
      } catch (error: unknown) {
        set((s) => {
          s.error = getApiErrorMessage(error, 'Failed to fetch spans');
          s.isLoadingSpans = false;
        });
      }
    },

    selectSpan: (span: SpanDetail | null) => {
      set((s) => { s.selectedSpan = span; });
    },

    clearSelection: () => {
      set((s) => {
        s.selectedThread = null;
        s.selectedSpans = [];
        s.spanTree = [];
        s.selectedSpan = null;
      });
    },

    setFilter: (key: keyof TracesFilters, value: string) => {
      set((s) => {
        if (value) {
          s.filters[key] = value;
        } else {
          delete s.filters[key];
        }
        s.page = 0; // reset to first page on filter change
      });
    },

    setPage: (page: number) => {
      set((s) => { s.page = page; });
    },

    fetchStats: async (days?: number) => {
      try {
        const result = await tracesApi.getStats(days);
        set((s) => { s.stats = result.data; });
      } catch (error: unknown) {
        set((s) => { s.error = getApiErrorMessage(error, 'Failed to fetch stats'); });
      }
    },

    fetchConfig: async () => {
      try {
        const result = await tracesApi.getConfig();
        set((s) => { s.traceLevelConfigs = result.data; });
      } catch (error: unknown) {
        set((s) => { s.error = getApiErrorMessage(error, 'Failed to fetch config'); });
      }
    },

    setTraceLevel: async (scope: string, scopeId: string, traceLevel: number) => {
      try {
        await tracesApi.setConfig({ scope, scopeId, traceLevel });
        await get().fetchConfig();
      } catch (error: unknown) {
        set((s) => { s.error = getApiErrorMessage(error, 'Failed to set trace level'); });
      }
    },

    deleteTraceLevel: async (scope: string, scopeId: string) => {
      try {
        await tracesApi.deleteConfig(scope, scopeId);
        await get().fetchConfig();
      } catch (error: unknown) {
        set((s) => { s.error = getApiErrorMessage(error, 'Failed to delete trace level'); });
      }
    },
  }))
);
